/*
 * The invitation somebody arrived on, held on the device until there is an
 * account to attach it to (docs/5 § D260805-0328).
 *
 * WHY THIS EXISTS AT ALL, which is the only interesting thing about the file. A
 * `/join/:code` link is opened by somebody with no account, and the code has to
 * survive the whole of signing up to reach `POST /me/onboarding` — which means
 * surviving a full navigation away and back: `startSocialSignIn` sends the browser
 * to Google, and the provider returns to the app's ORIGIN rather than to the
 * `/join` URL. So the code cannot live in a route parameter, in React state, or in
 * the query cache. It has to be written down somewhere the round trip does not
 * clear, and then read once at the end.
 *
 * `localStorage` RATHER THAN `sessionStorage`: on iOS the OAuth handshake happens
 * in the system browser and comes back into the app, which is a different
 * browsing context, and a session store is scoped to a tab. It is also the reason
 * this is not `@capacitor/preferences` (where docs/8 § G keeps letter drafts): that
 * API is async, and both readers need a value synchronously — one while building a
 * request body (`OnboardingScreen`), one inside a `useState` initialiser
 * (`SignInScreen`).
 *
 * WHO WRITES AND WHO CLEARS, because it is not the screen you would guess.
 * `AppShell` does both (`useInviteHandoff`): it stores from the pathname before the
 * login gate branches — `JoinScreen` does not render for somebody with a half-made
 * account — and it clears the moment a finished viewer exists, because a stored
 * invitation outliving its purpose is how a shared device attributes one person's
 * signup to somebody else's link. `OnboardingScreen` also clears on a successful
 * claim, which is the ordinary path.
 *
 * EVERY ACCESS IS WRAPPED. `localStorage` THROWS rather than returning null when
 * storage is denied — Safari in a locked-down configuration, an embedded WebView
 * with data blocked — and an exception here would take down the sign-in screen for
 * somebody whose only problem is that an invitation cannot be remembered. A
 * forgotten invitation costs a follow and a notification; a thrown one costs the
 * account.
 */

const KEY = 'dove.invite';

/** Note the invitation, when a `/join/:code` link is opened. */
export function rememberInvite(code: string): void {
  try {
    window.localStorage.setItem(KEY, code);
  } catch {
    /* Nothing to report and nothing to retry: the person still signs up, they
       just arrive unattributed. */
  }
}

/** The invitation waiting to be spent, if there is one. */
export function recallInvite(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/**
 * Spend it.
 *
 * CALLED ONCE ONBOARDING HAS SUCCEEDED, never before. The server treats a code it
 * cannot resolve as no code at all, so a failed claim is safe to retry — and
 * clearing this on the attempt rather than on the result would lose a real
 * invitation to one flaky request.
 */
export function forgetInvite(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* If it cannot be removed it was never stored. */
  }
}
